// =============================================================================
// Public Booking - React Query hooks
// =============================================================================
// Hook usati dal wizard pubblico e dal widget embed. Path:
//   /api/v1/public/:tenantSlug/{tenant,services,availability,bookings}
// =============================================================================

import { useQuery, useMutation } from '@tanstack/react-query';
import { publicApiClient } from './public-client';

// -----------------------------------------------------------------------------
// Types che riflettono le response del backend (vedi public-booking.dto.ts)
// -----------------------------------------------------------------------------

export interface PublicTenantInfo {
  slug: string;
  displayName: string;
  branding: {
    logoUrl: string | null;
    /// Icona standard dell'organizzazione, usata quando non c'è un logo
    icon: string;
    primaryColor: string | null;
    theme: 'minimal' | 'hero';
    coverImageUrl: string | null;
    headline: string | null;
    subheadline: string | null;
  };
  termsUrl: string | null;
  privacyUrl: string | null;
  captcha: {
    enabled: boolean;
    provider: string | null;
    siteKey: string | null;
  };
  /// Timezone IANA dell'attivita'. Gli orari mostrati al cliente sono SEMPRE
  /// espressi in questo fuso ed etichettati: l'appuntamento e' fisico, e
  /// convertirlo nel fuso del visitatore lo farebbe presentare all'ora sbagliata.
  timezone: string;
  defaultMode: 'auto' | 'approval';
  minAdvanceHours: number;
  maxAdvanceDays: number;
  allowGuestCustomers: boolean;
}

export interface PublicService {
  id: string;
  name: string;
  description: string | null;
  durationMinutes: number;
  basePrice: number | null;
  color: string | null;
  category: string | null;
  requiresClient: boolean;
  allowResourceSelection: boolean;
  mode: 'auto' | 'approval';
  selectableResources?: Array<{
    resourceTypeId: string;
    resourceTypeName: string;
    resources: Array<{ id: string; name: string; color: string | null }>;
  }>;
}

export interface PublicSlot {
  start: string;
  end: string;
  allocations?: Array<{
    resourceTypeId: string;
    resourceId: string;
    startTime: string;
    endTime: string;
    role: string;
  }>;
}

export interface SubmitBookingPayload {
  serviceId: string;
  startTime: string;
  preferredAllocations?: Array<{ resourceTypeId: string; resourceId?: string }>;
  customer: {
    firstName: string;
    lastName: string;
    email?: string;
    phone?: string;
    fiscalCode?: string;
    dateOfBirth?: string;
    notes?: string;
  };
  captchaToken?: string;
  locale?: string;
  source?: 'web' | 'widget';
  _hp?: string; // honeypot
  acceptTerms: boolean;
}

export interface SubmitBookingResult {
  outcome: 'auto' | 'approval';
  appointmentId?: string;
  bookingRequestId?: string;
  token: string;
  scheduledStart: string;
  scheduledEnd: string;
}

// -----------------------------------------------------------------------------
// Hooks
// -----------------------------------------------------------------------------

export function usePublicTenantInfo(slug: string | undefined) {
  return useQuery({
    queryKey: ['public', slug, 'tenant'],
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<PublicTenantInfo> => {
      const { data } = await publicApiClient.get(`/public/${slug}/tenant`);
      return data;
    },
    retry: false,
  });
}

export function usePublicServices(slug: string | undefined) {
  return useQuery({
    queryKey: ['public', slug, 'services'],
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<PublicService[]> => {
      const { data } = await publicApiClient.get(`/public/${slug}/services`);
      return data;
    },
  });
}

export function usePublicAvailability(
  slug: string | undefined,
  params: { serviceId: string; from: string; to: string } | null,
) {
  return useQuery({
    queryKey: ['public', slug, 'availability', params],
    enabled: !!slug && !!params,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<PublicSlot[]> => {
      const { data } = await publicApiClient.get(`/public/${slug}/availability`, { params });
      return data;
    },
  });
}

export function useSubmitPublicBooking(slug: string | undefined) {
  return useMutation({
    mutationFn: async (payload: SubmitBookingPayload): Promise<SubmitBookingResult> => {
      const { data } = await publicApiClient.post(`/public/${slug}/bookings`, payload);
      return data;
    },
  });
}
